import { Message } from '../chat/message';

export class ApiError extends Error {
    statusCode : number;
    code       : string;

  constructor(statusCode : number, code : string, message: string) {
    super(message);
    this.statusCode = statusCode;
    this.code       = code;

    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export interface IMessageTree
{
    role      : Role;
    content   : string;
    rating    : number;
    timestamp : string;
    selected  : number;
    replies   : IMessageTree[];
}

export enum Role {
  User      = 'user',
  Assistant = 'assistant',
  System    = 'system'
}

export class ChatError extends Error {
    msg: Message;

    constructor(message: string) {
        super(message);
        this.msg = new Message(Role.Assistant, message);
    }

    getMessage(): Message {
        return this.msg;
    }
}

export interface ISaveChat
{
    id?         : string,
    name        : string,
    tree?       : IMessageTree
}

export interface IChatInfo
{
    active?      : boolean,
    id           : string,
    name         : string,
    description  : string,
    timestamp    : Date
}

export interface IChat
{
    id           : string,
    name         : string,
    description  : string,
    tree         : IMessageTree
}

export interface IModel
{
    id             : string,
    name           : string,
    description    : string,
    parent_model   : string | undefined,
    learning_rate  : number,
    learning_steps : number,
    epsilon        : number,
    beta           : number,
    temperature    : number,
    topP           : number,
    method         : TrainingMethod;
    last_modified  : string;
    default_model  : boolean;
    read_only      : boolean;
}

export interface INewModel
{
    name           : string,
    description    : string,
    parent_model   : string,
    rank?          : number,
    alpha?         : number,
    dropout?       : number,
    learning_rate? : number
};

export enum TrainingMethod {
    DPO  = "dpo",
    GRPO = "grpo",
    SFT  = "sft",
    GCE  = "gce"
};

export interface ITraining {
    method           : TrainingMethod;
    epsilon?         : number;
    beta?            : number;
    learning_rate?   : number;
    learning_steps?  : number;
    target_accuracy? : number;
};

export interface ITrainingPerformance
{
    id        : number;
    advantage : number;
    accuracy  : number;
    loss      : number;
    step_no   : number;
}

export interface ITrainingUsage
{
    input_tokens    : number;
    output_tokens   : number;
    num_samples     : number;
    steps           : number;
    training_tokens : number;
    duration_ms     : number;
}

export interface ITrainingInfoChunk
{
    type    : "info";
    message : string;
}

export interface ITrainingPerformanceChunk
{
    type        : "performance";
    performance : ITrainingPerformance;
}

export interface ITrainingUsageChunk
{
    type  : "usage";
    usage : ITrainingUsage;
}

export interface ITrainingErrorChunk
{
    type    : "error";
    message : string;
}

export type ITrainingChunk =
    | ITrainingInfoChunk
    | ITrainingPerformanceChunk
    | ITrainingUsageChunk
    | ITrainingErrorChunk;

export interface IState
{
    playgroundRunning : boolean;
    user?             : IUser;
}

export interface IAPIKey
{
    token     : string;
    name      : string;
    created   : string;
    lastUsed? : string;
};

export interface IUser
{
    email   : string;
    apiKeys : IAPIKey[];
}

export interface IDeleteModelResponse
{
    id      : string;
    message : string;
}

export interface IBilling
{
    timestamp: string;
    type: string;
    tokenId: string;
    model: string;
    inputTokens: number;
    outputTokens: number;
    trainingTokens: number;
    durationMs: number;
    cost: number;
}

export interface BillingRecords {
    start?: Date;
    end?: Date;
    bills: IBilling[]
}

export interface ICheckoutSession
{
    checkoutUrl: string;
}

export enum PaymentStatus {
    paid = "0",
    pending = "-1",
    failed = "1"
}

export interface IPaymentStatus
{
    status: PaymentStatus
}

export interface TokenPrices {
    inputTokenPrice: string;
    outputTokenPrice: string;
    trainTokenPrice: string;
}

export interface LLMAPI
{
    changePassword?(email : string, password : string, token : string, payload : string) : Promise<void>;
    createAccount?(username : string, password : string, altcha : string) : Promise<void>;
    createApiKey?(name : string) : Promise<IAPIKey>;
    createModel?(model: INewModel): Promise<IModel>;
    deleteAPIKey?(shortToken : string) : Promise<void>;
    deleteChat?(id : string): Promise<void>;
    deleteModel?(model: IModel): Promise<IDeleteModelResponse[]>;
    forgotPassword?(email : string, altcha : string) : Promise<void>;
    getReply(model: IModel, messages: Message[]): Promise<Message>;
    getReplyStreaming?(model: IModel, messages: Message[], onPart: (part: string) => void): Promise<Message>;
    getState?() : Promise<IState>;
    loadChat?(chat: IChatInfo): Promise<IChat>;
    login?(email : string, password : string, altcha : string) : Promise<void>;
    logout?() : Promise<void>;
    verify?(email : string, password : string, emailCode : string, altcha : string) : Promise<void>;
    list?(): Promise<IChatInfo[]>;
    modelList?(): Promise<IModel[]>
    save?(chat: ISaveChat): Promise<string>;
    train?(model: IModel, query: Message[], replies: Message[], settings: ITraining, onProcess ? : (progress : ITrainingChunk) => void): Promise<void>;
    getBilling?(from: string, to: string): Promise<IBilling[]>;
    getCheckout?(amount: number): Promise<ICheckoutSession>;
    getPaymentStatus?(checkoutId: string): Promise<IPaymentStatus>;
    getCredits?(): Promise<number>;
    getTokenPrices?(): Promise<TokenPrices>;
}
